import type { StockQuantityError } from '@/shared/lib/stock-quantity'
import {
  formatStockQuantity,
  parseStockQuantity,
} from '@/shared/lib/stock-quantity'

/**
 * The parts editor of the "Lagerverbrauch" tab (CHG-2026-09-stock-usage-and-price-twins, 10.3):
 * the rows a manager edits, and the checks run before the list goes to the server. The server
 * repeats every check; these only keep an obviously wrong list from leaving the browser.
 */

/** One row as edited: the quantity stays the typed text until it is saved. */
export interface PartRow {
  sku: string
  name: string
  unit: string
  stockTracked: boolean
  qtyInput: string
}

export type PartRowError = StockQuantityError | 'duplicate' | 'self'

export type PartsValidation =
  | { ok: true; parts: Array<{ sku: string; qty: number }> }
  | { ok: false; errors: Record<number, PartRowError> }

/** Validates the rows of `dishSku`'s usage list; an empty list is valid (it removes the list). */
export function validateParts(
  rows: Array<PartRow>,
  dishSku: string,
): PartsValidation {
  const errors: Record<number, PartRowError> = {}
  const parts: Array<{ sku: string; qty: number }> = []
  const seen = new Set<string>()
  rows.forEach((row, index) => {
    if (row.sku === dishSku) {
      errors[index] = 'self'
      return
    }
    if (seen.has(row.sku)) {
      errors[index] = 'duplicate'
      return
    }
    seen.add(row.sku)
    const parsed = parseStockQuantity(row.qtyInput)
    if (!parsed.ok) {
      errors[index] = parsed.error
      return
    }
    parts.push({ sku: row.sku, qty: parsed.value })
  })
  return Object.keys(errors).length > 0
    ? { ok: false, errors }
    : { ok: true, parts }
}

/**
 * Whether a searched product may be offered as a part. The search asks the server for stock
 * articles only (`stockArticlesOnly`: no dishes, variants, merged duplicates or deleted products,
 * with a correct total), so only the dish itself and the rows already listed remain to drop.
 */
export function isPartCandidate(
  product: { sku: string },
  dishSku: string,
  listedSkus: ReadonlySet<string>,
): boolean {
  return product.sku !== dishSku && !listedSkus.has(product.sku)
}

/** The rows a saved list starts the editor with. */
export function rowsFromParts(
  parts: Array<{
    sku: string
    name: string
    qty: number
    unit: string
    stockTracked: boolean
  }>,
): Array<PartRow> {
  return parts.map((part) => ({
    sku: part.sku,
    name: part.name,
    unit: part.unit,
    stockTracked: part.stockTracked,
    qtyInput: formatStockQuantity(part.qty),
  }))
}

/** Whether the edited rows differ from the saved list (same SKUs in order, same quantities). */
export function partsChanged(
  rows: Array<PartRow>,
  saved: Array<{ sku: string; qty: number }>,
): boolean {
  if (rows.length !== saved.length) return true
  return rows.some((row, index) => {
    const parsed = parseStockQuantity(row.qtyInput)
    return (
      row.sku !== saved[index].sku ||
      !parsed.ok ||
      parsed.value !== saved[index].qty
    )
  })
}
